import { AppButton, AppDialog, FormSection } from "../../../../../components/ui";
import {
  CSADisplayText,
  errorInstructions,
  LeadType,
  ReasonForDeclinePIDisplayText,
  ReasonForDeclineWCDisplayText,
  ReasonForKeepPIDisplayText,
  ReasonForNAEmpDisplayMapping,
  ReasonForReferPIDisplayText,
} from "../../shared/Constants";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import { LeadsService } from "../../../../../api/services/LeadsService";
import { AttorneyApprovalStatus } from "../../../../../api/models/AttorneyApprovalStatus";
import { HelpersService } from "../../../../../services/Helpers";
import { UpdateApprovalStatusRequest } from "../../../../../api/models/UpdateApprovalStatusRequest";
import useNavigateSearch from "../../../../../services/useNavigateSearch";
import InputLabel from "@mui/material/InputLabel/InputLabel";
import Select from "@mui/material/Select/Select";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import { LeadViewModel } from "../../../../../api/models/LeadViewModel";
import {
  AdminLeadStatus,
  Category,
  CSAType,
  LeadCommentRequest,
  OpenAPI,
  ReasonForDeclinePI,
  ReasonForKeepPI,
  ReasonForNAEmp,
  ReasonForNgcWC,
  ReasonForReferPI,
} from "../../../../../api";
import FormControl from "@mui/material/FormControl/FormControl";
import CommentsTab from "./CommentsTab";
import TextField from "@mui/material/TextField/TextField";
import { useDispatch } from "react-redux";
import {
  startSpinner,
  stopSpinner,
} from "../../../../../redux/actions/spinnerActions";
import { Stack, Typography } from "@mui/material";
import LeadSideRailPortal from "./LeadSideRailPortal";
import { useState } from "react";
import { ApplicationRole } from "../../../../../api/models/ApplicationRole";

const MySwal = withReactContent(Swal);

interface AttorneyApprovalActionsProps {
  leadType: LeadType;
  userRoles?: ApplicationRole[];
  details?: LeadViewModel;
}

export default function AttorneyApprovalActions(
  props: AttorneyApprovalActionsProps
) {
  const [empRejectReason, setEmpRejectReason] = useState<ReasonForNAEmp>();
  const [selectedCSA, setSelectedCSA] = useState<CSAType>();
  const [CSAComment, setCSAComment] = useState<string>();
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false);
  const [approvalValue, setApprovalValue] = useState<AttorneyApprovalStatus>();
  const [openComments, setOpenComments] = useState<boolean>(false);

  const [piRejectReason, setPiRejectReason] = useState<ReasonForDeclinePI>();
  const [otherReasoning, setOtherReasoning] = useState<string>();
  const [piReferReason, setPiReferReason] = useState<ReasonForReferPI>();
  const [piKeepReason, setPiKeepReason] = useState<ReasonForKeepPI>();
  const [autoComment, setAutoComment] = useState<string>();

  const [wcRejectReason, setWcRejectReason] = useState<ReasonForNgcWC>();
  const [approvalQuality, setApprovalQuality] = useState<"" | "Low" | "High">("");

  const navigate = useNavigateSearch();
  const dispatch = useDispatch();

  const triggerCommentsOpen = (value) => {
    value
      ? setApprovalValue(AttorneyApprovalStatus.MORE_INFO_REQUESTED)
      : setApprovalValue(AttorneyApprovalStatus.IN_PROCESS);
    setOpenComments(value);
  };

  const handleApprovalChange = (status: AttorneyApprovalStatus) => {
    setApprovalValue(status);
    setApprovalQuality("");
    setOpenApprovalDialog(true);
  };

  const handleApprovalDialogClose = (success) => {
    if (success) {
      if (!props.details?.id) {
        setOpenApprovalDialog(false);
        return;
      }
      var approvalRequest = {
        attorneyApprovalStatus: approvalValue,
        reasoningDeclineEmp: empRejectReason
          ? ReasonForNAEmp[empRejectReason]
          : null,
        reasoningDeclinePI: piRejectReason
          ? ReasonForDeclinePI[piRejectReason]
          : null,
        otherReasoning: otherReasoning,
        reasoningKeepPI: piKeepReason ? ReasonForKeepPI[piKeepReason] : null,
        reasoningReferPI: piReferReason
          ? ReasonForReferPI[piReferReason]
          : null,
        referOtherPI: autoComment,
        reasoningNgcWC: wcRejectReason ? ReasonForNgcWC[wcRejectReason] : null,
        csaType: selectedCSA ? CSAType[selectedCSA] : null,
        approvalType:
          approvalValue === AttorneyApprovalStatus.KEEP
            ? approvalQuality || null
            : null,
      } as UpdateApprovalStatusRequest;

      dispatch(startSpinner());
      LeadsService.postApiLeadsApprovalChange(
        props.details?.id,
        approvalRequest
      )
        .then(() => {
          if (approvalValue === AttorneyApprovalStatus.KEEP && !!selectedCSA) {
            LeadsService.postApiLeadsLeaveComment(props.details!.id!, {
              message:
                "Attorney recommended " + CSADisplayText[CSAType[selectedCSA]],
              sendEmail: false,
              updateStatus: false,
            } as LeadCommentRequest).then();
          }
          if (approvalValue === AttorneyApprovalStatus.KEEP && !!CSAComment) {
            LeadsService.postApiLeadsLeaveComment(props.details!.id!, {
              message: CSAComment,
              sendEmail: false,
              updateStatus: false,
            } as LeadCommentRequest).then();
          }

          if (approvalValue === AttorneyApprovalStatus.KEEP && !!props.details?.classDetails) {
            LeadsService.postApiLeadsLeaveComment(props.details!.id!, {
              message:
                "Attorney approved Class Action",
              sendEmail: false,
              updateStatus: false,
            } as LeadCommentRequest).then();
          }

          const wasApproveAttorneyReject =
            approvalValue === AttorneyApprovalStatus.APPROVE_REJECT;

          MySwal.fire({
            icon: "success",
            title: <p className="title">Success!</p>,
            html: (
              <div className="alert-text">
                {approvalValue === AttorneyApprovalStatus.MORE_INFO_REQUESTED
                  ? "You will be notified when staff answers."
                  : wasApproveAttorneyReject
                  ? "You have approved attorney rejection."
                  : "You have finished review of this lead."}
              </div>
            ),
          }).then(() => {
            if (wasApproveAttorneyReject) {
              navigate(`/archive`, { status: AdminLeadStatus.ATTORNEY_REJECT });
              return;
            }
            navigate(`/approve-leads`);
          });
        })
        .catch(() => {
          MySwal.fire({
            icon: "error",
            title: <p className="title">Error updating client approval</p>,
            html: <div className="alert-text">{errorInstructions}</div>,
          });
        })
        .finally(() => dispatch(stopSpinner()));
    }
    setOpenApprovalDialog(false);
  };

  const generateReasonDetails = (reason: string) => {
    switch (reason) {
      //PI Keep
      case ReasonForKeepPI.AUTO:
        return (
          <div className="row mt-3 text-start">
            <h5>Please make sure these apply:</h5>
            <ul>
              <li>Commercial Insurance - 500K - 2M</li>
              <li>Liability is Clear (Not clients fault)</li>
              <li>No Treatment Gap - Treatment on Day or Week of Accident</li>
              <li>Lead has Auto Insurance</li>
              <li>SOL within 2 years </li>
            </ul>
          </div>
        );
      case ReasonForKeepPI.PREMISES_LIABILITY:
        return (
          <div className="row mt-3 text-start">
            <h5>Please make sure these apply:</h5>
            <ul>
              <li>Liability is Clear (Not clients fault)</li>
              <li>
                Typically good to keep
                <ul>
                  <li>Object falling on Lead</li>
                  <li>Fire or Explosion</li>
                  <li>Anything that has Wrongful Death</li>
                </ul>
              </li>
            </ul>
          </div>
        );
      case ReasonForKeepPI.MED_MAL:
        return (
          <div className="row mt-3 text-start">
            <ul>Brendan reviews and decides</ul>
          </div>
        );
      //PI Refer
      case ReasonForReferPI.NBCAUTO:
        return (
          <div className="row mt-3 text-start">
            <h5>One of these apply:</h5>
            <ul>
              <li>
                Liability is Unclear (Police or adjuster says client caused
                accident)
              </li>
              <li>Gap in Treatment (More than 1 week)</li>
              <li>Lead is Uninsured or Defendant is Uninsured </li>
              <li>Substitution of Attorneys</li>
            </ul>
            <TextField
              required
              label="Auto issue"
              multiline
              variant="outlined"
              value={autoComment}
              onChange={(e) => setAutoComment(e.target.value)}
            />
          </div>
        );
      case ReasonForReferPI.EASY_AUTO:
        return (
          <div className="row mt-3 text-start">
            <h5>Please make sure these apply:</h5>
            <ul>
              <li>Liability is Clear (Not clients fault)</li>
              <li>
                Defendant has insurance or Client has UM/UIM (Uninsured
                Motorist/Underinsured Motorist)
              </li>
              <li>No Treatment Gap - Treatment on Day or Week of Accident</li>
              <li>
                Clients Policy
                <ul>
                  <li>15/30 (15k-30k)</li>
                  <li>25/50 (25k-50k)</li>
                  <li>100/300 (100k) 100 - per person/300 - per accident</li>
                </ul>
              </li>
            </ul>
          </div>
        );
      case ReasonForReferPI.EASY_PREMISES_LIABILITY:
        return (
          <div className="row mt-3 text-start">
            <h5>Please make sure these apply:</h5>
            <ul>
              <li>Has photos of the premises or surveillance</li>
              <li>No Treatment Gap - Treatment on Day or Week of Accident</li>
              <li>Notification was made to the premises </li>
              <li>No Prior Falls</li>
              <li>Typically we will refer out slip and falls </li>
            </ul>
          </div>
        );
      case ReasonForReferPI.CIVIL_RIGHTS:
        return (
          <div className="row mt-3 text-start">
            <h5>Please make sure these apply:</h5>
            <ul>
              <li>Police Brutality</li>
              <li>Government</li>
            </ul>
          </div>
        );
    }
  };
  const generateDeclineReasonDetails = (reason: string) => {
    switch (reason) {
      //PI Reject
      case ReasonForDeclinePI.AUTO:
        return (
          <div className="row mt-3 text-start">
            <h5>One or more apply:</h5>
            <ul>
              <li>Client has no insurance and Defendant has no insurance</li>
              <li>
                Liability disputed (Police or adjuster says client caused
                accident)
              </li>
              <li>Gap in Treatment - Over a month</li>
              <li>SOL over 2 years</li>
              <li>Evidence not strong enough</li>
              <li>Other - enter reason</li>
            </ul>
          </div>
        );
      case ReasonForDeclinePI.PREMISES_LIABILITY:
        return (
          <div className="row mt-3 text-start">
            <h5>One or more apply:</h5>
            <ul>
              <li>Gap in Treatment - Over a month</li>
              <li>No Photos</li>
              <li>No Surveillance</li>
              <li>SOL over 2 years</li>
              <li>Other - enter reason</li>
            </ul>
          </div>
        );
      case ReasonForDeclinePI.OTHER:
        return <div className="row mt-3 text-start">
          <div>Identify the Type of Case</div>
        </div>;
    }
  };

  const downloadClick = () => {
    if (!!props.details?.id) {
      fetch(`${OpenAPI.BASE}/api/Leads/${props.details.id}/pdfIntake`, {
        method: "GET",
        headers: {
          Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        },
      }).then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          if (props.details?.attorneyApprovalStatus === AttorneyApprovalStatus.REFER) {
            a.download = `Intake notes.pdf`;
          }
          else {
            a.download = `Intake notes for ${props.details?.firstName} ${props.details?.lastName}.pdf`;
          }
          document.body.appendChild(a);
          a.click();
        });
      });
    }
  }

  return (
    <>
      {((props.leadType === LeadType.StaffAttorney &&
        (props.userRoles?.includes(ApplicationRole.STAFF_ATTORNEY) || props.userRoles?.includes(ApplicationRole.REFFERAL_STAFF)))
        || (props.userRoles?.includes(ApplicationRole.ADMIN) && props.details?.isCurrentUserReviewing && props.details.attorneyApprovalStatus !== AttorneyApprovalStatus.REFER && props.details.attorneyApprovalStatus !== AttorneyApprovalStatus.KEEP && props.details.attorneyApprovalStatus !== AttorneyApprovalStatus.APPROVE_REJECT)) && (
          <LeadSideRailPortal>
            <FormSection
              className="lead-side-panel"
              title="Review actions"
              subtitle="Keep the case, refer it out, reject it, or request more information."
            >
              <Stack spacing={1.5}>
                {props.details?.category !== Category.WORKERS_COMPENSATION && (
                  <AppButton
                    variant="contained"
                    color="dirtyGreen"
                    fullWidth
                    onClick={() =>
                      handleApprovalChange(AttorneyApprovalStatus.KEEP)
                    }
                  >
                    Keep
                  </AppButton>
                )}
                {props.details?.category !== Category.EMPLOYMENT && props.details?.category !== Category.CLASS_ACTION && (
                  <AppButton
                    variant="contained"
                    color="purple"
                    fullWidth
                    onClick={() =>
                      handleApprovalChange(AttorneyApprovalStatus.REFER)
                    }
                  >
                    Refer
                  </AppButton>
                )}
                <AppButton
                  variant="contained"
                  color="errorColor"
                  fullWidth
                  onClick={() =>
                    handleApprovalChange(
                      props.details?.attorneyApprovalStatus === AttorneyApprovalStatus.REJECT
                        ? AttorneyApprovalStatus.APPROVE_REJECT
                        : AttorneyApprovalStatus.REJECT
                    )
                  }
                >
                  {props.details?.attorneyApprovalStatus === AttorneyApprovalStatus.REJECT
                    ? "Approve Attorney Reject"
                    : "Reject"}
                </AppButton>
                {props.details?.attorneyApprovalStatus ===
                  AttorneyApprovalStatus.IN_PROCESS && (
                    !openComments ? (
                      <AppButton
                        variant="contained"
                        color="warning"
                        fullWidth
                        onClick={() => triggerCommentsOpen(true)}
                      >
                        More info needed
                      </AppButton>
                    ) : (
                      <AppButton
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        onClick={() => triggerCommentsOpen(false)}
                      >
                        Cancel info request
                      </AppButton>
                    )
                  )}
                {props.details?.category === Category.PERSONAL_INJURY && (
                  <AppButton
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    onClick={downloadClick}
                  >
                    Download Pdf
                  </AppButton>
                )}
              </Stack>
            </FormSection>
          </LeadSideRailPortal>
        )}
      <AppDialog
                    open={openApprovalDialog}
                    onClose={() => handleApprovalDialogClose(false)}
                    sx={{
                      "& .MuiDialog-paper": { textAlign: "left" },
                      "& .MuiDialogTitle-root": {
                        textAlign: "left",
                        fontSize: "1.125rem",
                        fontWeight: 600,
                        pb: 0,
                      },
                      "& .MuiDialogContent-root": { pt: 1.5 },
                      "& .MuiDialogActions-root": { justifyContent: "flex-end", px: 3, pb: 2 },
                    }}
                    title="Confirm your choice"
                    actions={
                      <>
                        <AppButton
                          color="gray"
                          variant="contained"
                          onClick={() => handleApprovalDialogClose(false)}
                        >
                          Cancel
                        </AppButton>
                        <AppButton
                          disabled={
                            (approvalValue === AttorneyApprovalStatus.KEEP &&
                              !approvalQuality) ||
                            (approvalValue === AttorneyApprovalStatus.KEEP &&
                              !selectedCSA &&
                              !piKeepReason && props.details?.category !== Category.WORKERS_COMPENSATION && props.details?.category !== Category.CLASS_ACTION) ||
                            (approvalValue === AttorneyApprovalStatus.REJECT &&
                              !empRejectReason &&
                              !piRejectReason && !wcRejectReason && props.details?.category !== Category.CLASS_ACTION) ||
                            (props.details?.category ===
                              Category.PERSONAL_INJURY &&
                              approvalValue === AttorneyApprovalStatus.REFER &&
                              !piReferReason) ||
                            (props.details?.category ===
                              Category.PERSONAL_INJURY &&
                              approvalValue === AttorneyApprovalStatus.REFER &&
                              piReferReason &&
                              ReasonForReferPI[piReferReason] ===
                              ReasonForReferPI.NBCAUTO &&
                              !autoComment) ||
                            (props.details?.category ===
                              Category.CLASS_ACTION &&
                              approvalValue === AttorneyApprovalStatus.REJECT &&
                              !otherReasoning)
                          }
                          color={
                            approvalValue === AttorneyApprovalStatus.KEEP
                              ? "dirtyGreen"
                              : approvalValue === AttorneyApprovalStatus.REFER
                                ? "purple"
                                : approvalValue === AttorneyApprovalStatus.REJECT ||
                                    approvalValue === AttorneyApprovalStatus.APPROVE_REJECT
                                  ? "errorColor"
                                  : "primary"
                          }
                          variant="contained"
                          onClick={() => handleApprovalDialogClose(true)}
                        >
                          {HelpersService.camelToString(approvalValue)}
                        </AppButton>
                      </>
                    }
                  >
                      <Stack spacing={2} sx={{ pt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {approvalValue === AttorneyApprovalStatus.KEEP &&
                        props.details?.category === Category.EMPLOYMENT
                          ? "Select quality and CSA type."
                          : approvalValue === AttorneyApprovalStatus.KEEP &&
                              props.details?.category === Category.PERSONAL_INJURY
                            ? "Select quality and a reason."
                            : approvalValue === AttorneyApprovalStatus.KEEP
                              ? "Select quality."
                              : "Select a reason."}
                      </Typography>
                      {approvalValue === AttorneyApprovalStatus.KEEP && (
                        <FormControl fullWidth>
                          <InputLabel id="approval-quality-label">
                            Quality
                          </InputLabel>
                          <Select
                            labelId="approval-quality-label"
                            name="approvalQuality"
                            label="Quality"
                            value={approvalQuality}
                            onChange={(e) =>
                              setApprovalQuality(e.target.value as "Low" | "High")
                            }
                          >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                      {props.details?.category === Category.EMPLOYMENT && (
                        <>
                          {approvalValue === AttorneyApprovalStatus.REJECT && (
                            <div className="col-12 mt-3">
                              <FormControl fullWidth>
                                <InputLabel id="selected-label">
                                  Select reason*
                                </InputLabel>
                                <Select
                                  labelId="selected-label"
                                  name="empRejectReason"
                                  label="Select reason"
                                  value={empRejectReason}
                                  key={empRejectReason}
                                  onChange={(e) =>
                                    setEmpRejectReason(
                                      e.target.value as ReasonForNAEmp
                                    )
                                  }
                                >
                                  {Object.keys(ReasonForNAEmp).map((reason) => {
                                    return (
                                      <MenuItem key={reason} value={reason}>
                                        {
                                          ReasonForNAEmpDisplayMapping[
                                          ReasonForNAEmp[reason]
                                          ]
                                        }
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                                <div className="col-12 mt-3">
                                  {empRejectReason &&
                                    ReasonForNAEmp[empRejectReason] ===
                                    ReasonForNAEmp.OTHER && (
                                      <TextField
                                        fullWidth
                                        required
                                        label="Describe Other"
                                        variant="outlined"
                                        value={otherReasoning}
                                        onChange={(e) =>
                                          setOtherReasoning(e.target.value)
                                        }
                                      />
                                    )}
                                </div>
                              </FormControl>
                            </div>
                          )}
                          {approvalValue === AttorneyApprovalStatus.KEEP && (
                            <Stack spacing={2}>
                              <FormControl fullWidth>
                                <InputLabel id="selected-label">
                                  CSA type
                                </InputLabel>
                                <Select
                                  labelId="selected-label"
                                  name="selectedCSA"
                                  label="CSA type"
                                  value={selectedCSA}
                                  key={selectedCSA}
                                  onChange={(e) =>
                                    setSelectedCSA(e.target.value as CSAType)
                                  }
                                >
                                  {Object.keys(CSAType).map((reason) => {
                                    return (
                                      <MenuItem key={reason} value={reason}>
                                        {CSADisplayText[CSAType[reason]]}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                              <TextField
                                fullWidth
                                label="Comment"
                                multiline
                                minRows={3}
                                variant="outlined"
                                value={CSAComment}
                                onChange={(e) =>
                                  setCSAComment(e.target.value)
                                }
                              />
                            </Stack>
                          )}
                        </>
                      )}

                      {props.details?.category === Category.PERSONAL_INJURY && (
                        <>
                          {approvalValue === AttorneyApprovalStatus.KEEP && (
                            <>
                              <div className="mt-3">
                                <FormControl>
                                  <InputLabel id="selected-label">
                                    Select Reason*
                                  </InputLabel>
                                  <Select
                                    labelId="selected-label"
                                    name="piKeepReason"
                                    className="min-width-200"
                                    label="Select reason"
                                    value={piKeepReason}
                                    key={piKeepReason}
                                    onChange={(e) =>
                                      setPiKeepReason(
                                        e.target.value as ReasonForKeepPI
                                      )
                                    }
                                  >
                                    {Object.keys(ReasonForKeepPI).map(
                                      (reason) => {
                                        return (
                                          <MenuItem key={reason} value={reason}>
                                            {
                                              ReasonForKeepPIDisplayText[
                                              ReasonForKeepPI[reason]
                                              ]
                                            }
                                          </MenuItem>
                                        );
                                      }
                                    )}
                                  </Select>
                                </FormControl>
                              </div>
                              {piKeepReason &&
                                generateReasonDetails(
                                  ReasonForKeepPI[piKeepReason]
                                )}
                            </>
                          )}
                          {approvalValue === AttorneyApprovalStatus.REJECT && (
                            <>
                              <div className="mt-3">
                                <FormControl>
                                  <InputLabel id="selected-label">
                                    Select Reason*
                                  </InputLabel>
                                  <Select
                                    labelId="selected-label"
                                    name="piRejectReason"
                                    className="min-width-200"
                                    label="Select reason"
                                    value={piRejectReason}
                                    key={piRejectReason}
                                    onChange={(e) =>
                                      setPiRejectReason(
                                        e.target.value as ReasonForDeclinePI
                                      )
                                    }
                                  >
                                    {Object.keys(ReasonForDeclinePI).map(
                                      (reason) => {
                                        return (
                                          <MenuItem key={reason} value={reason}>
                                            {
                                              ReasonForDeclinePIDisplayText[
                                              ReasonForDeclinePI[reason]
                                              ]
                                            }
                                          </MenuItem>
                                        );
                                      }
                                    )}
                                  </Select>
                                </FormControl>
                              </div>
                              {piRejectReason &&
                                generateDeclineReasonDetails(
                                  ReasonForDeclinePI[piRejectReason]
                                )}
                              {piRejectReason
                                && ReasonForDeclinePI[piRejectReason] !== ReasonForDeclinePI.MED_MAL
                                && (
                                  <TextField
                                    className="mt-3"
                                    fullWidth
                                    required
                                    label="Describe Other"
                                    variant="outlined"
                                    value={otherReasoning}
                                    onChange={(e) =>
                                      setOtherReasoning(e.target.value)
                                    }
                                  />
                                )}
                            </>
                          )}
                          {approvalValue === AttorneyApprovalStatus.REFER && (
                            <>
                              <div className="mt-3">
                                <FormControl>
                                  <InputLabel id="selected-label">
                                    Select Reason*
                                  </InputLabel>
                                  <Select
                                    labelId="selected-label"
                                    name="piReferReason"
                                    className="min-width-200"
                                    label="Select reason"
                                    value={piReferReason}
                                    key={piReferReason}
                                    onChange={(e) =>
                                      setPiReferReason(
                                        e.target.value as ReasonForReferPI
                                      )
                                    }
                                  >
                                    {Object.keys(ReasonForReferPI).map(
                                      (reason) => {
                                        return (
                                          <MenuItem key={reason} value={reason}>
                                            {
                                              ReasonForReferPIDisplayText[
                                              ReasonForReferPI[reason]
                                              ]
                                            }
                                          </MenuItem>
                                        );
                                      }
                                    )}
                                  </Select>
                                </FormControl>
                              </div>
                              {piReferReason &&
                                generateReasonDetails(
                                  ReasonForReferPI[piReferReason]
                                )}
                            </>
                          )}
                        </>
                      )}
                      {props.details?.category ===
                        Category.WORKERS_COMPENSATION && (
                          <>
                            {approvalValue === AttorneyApprovalStatus.REJECT && (
                              <>
                                <div className="mt-3">
                                  <FormControl>
                                    <InputLabel id="selected-label">
                                      Select Reason*
                                    </InputLabel>
                                    <Select
                                      labelId="selected-label"
                                      name="wcRejectReason"
                                      className="min-width-200"
                                      label="Select reason"
                                      value={wcRejectReason}
                                      key={wcRejectReason}
                                      onChange={(e) =>
                                        setWcRejectReason(
                                          e.target.value as ReasonForNgcWC
                                        )
                                      }
                                    >
                                      {Object.keys(ReasonForNgcWC).map(
                                        (reason) => {
                                          return (
                                            <MenuItem key={reason} value={reason}>
                                              {
                                                ReasonForDeclineWCDisplayText[
                                                ReasonForNgcWC[reason]
                                                ]
                                              }
                                            </MenuItem>
                                          );
                                        }
                                      )}
                                    </Select>
                                  </FormControl>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      {props.details?.category === Category.CLASS_ACTION && (
                        <>
                          {approvalValue === AttorneyApprovalStatus.REJECT && (
                            <div className="col-12 mt-3">
                              <TextField
                                fullWidth
                                required
                                label="Describe Reason"
                                variant="outlined"
                                value={otherReasoning}
                                onChange={(e) =>
                                  setOtherReasoning(e.target.value)
                                }
                              />
                            </div>
                          )}
                          {approvalValue === AttorneyApprovalStatus.KEEP && (
                            <TextField
                              fullWidth
                              label="Comment"
                              multiline
                              minRows={3}
                              variant="outlined"
                              value={CSAComment}
                              onChange={(e) =>
                                setCSAComment(e.target.value)
                              }
                            />
                          )}
                        </>
                      )}
                      </Stack>
                  </AppDialog>
      {(props.leadType === LeadType.Admin ||
        props.leadType === LeadType.StaffAttorney) &&
        (props.details?.attorneyApprovalStatus ===
          AttorneyApprovalStatus.MORE_INFO_PROVIDED ||
          props.details?.attorneyApprovalStatus ===
          AttorneyApprovalStatus.MORE_INFO_REQUESTED ||
          props.details?.attorneyApprovalStatus ===
          AttorneyApprovalStatus.KEEP ||
          openComments) && (
          <CommentsTab
            leadId={props.details?.id}
            comments={props.details?.comments}
            isNewRequest={openComments}
            onApprovalChange={handleApprovalDialogClose}
            hideActions={false}
            lowPlacement={
              props.details?.attorneyApprovalStatus ===
              AttorneyApprovalStatus.KEEP
            }
          ></CommentsTab>
        )}
    </>
  );
}
